import { type ReactNode } from "react";

import { ExternalLink } from "lucide-react";

import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { fmtCur, fmtCur2, fmtCurExact, fmtDate } from "../../renewals/format";
import {
  hubspotDealUrl,
  pandadocUrl,
  qboCustomerUrl,
  stripeCustomerUrl,
} from "../links";
import {
  useAccountDetail,
  useCompanyDetail,
  useContractDetail,
  useContractTypePlaybooks,
  useContractTypes,
  useReplacementValue,
  type ContractDetail,
  type ContractDiscount,
  type DiscountRow,
  type PackageRow,
  type RelatedContractRef,
} from "../api";
import {
  categoryLabel,
  discountTypeLabel,
  discountValueLabel,
  integrationLabels,
  marginPct,
  pricingTypeLabel,
} from "../catalog";
import { CreateProposalFromContractButton } from "@/features/proposals/components/CreateProposalFromContract";
import { ContractTimelineChart } from "./ContractTimelineChart";
import { ContractTypePlaybookView, ContractTypeQuickView } from "../playbook/PlaybookView";
import type { ContractsTabKey, DetailKind } from "..";

/** The record a row-click opened. For URL-driven kinds (contracts/accounts/
 *  companies) `row` is empty and the detail is fetched by `id`; for the other
 *  tabs `row` is the list row already in hand. */
export interface Selection {
  kind: ContractsTabKey;
  id: string;
  row: Record<string, unknown>;
}

/** Navigate to another URL-driven detail (pushes history → back-button friendly). */
type OpenDetail = (kind: DetailKind, id: string) => void;


// ── small presentational helpers ─────────────────────────────────────────────
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function Grid({ children }: { children: ReactNode }) {
  return <dl className="grid grid-cols-2 gap-x-6 gap-y-3">{children}</dl>;
}

function Field({ label, children }: { label: string; children?: ReactNode }) {
  const empty = children == null || children === "" || children === "—";
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm text-foreground">{empty ? "—" : children}</dd>
    </div>
  );
}

function NavLink({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="truncate text-left text-brand underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
    >
      {children}
    </button>
  );
}

function ExtLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-mono text-xs text-brand underline-offset-2 hover:underline"
    >
      <span className="truncate">{children}</span>
      <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  );
}

const str = (v: unknown): string => (v == null || v === "" ? "—" : String(v));
const yesNo = (v: unknown): string => (v == null ? "—" : v ? "Yes" : "No");
const isActiveThrough = (through?: string | null): boolean =>
  !through || through.slice(0, 10) > new Date().toISOString().slice(0, 10);

const RENEWAL_TERM_LABEL: Record<string, string> = {
  SAME_TERM: "Same term",
  DIFFERENT_TERM: "Different term",
  MONTH_TO_MONTH: "Month-to-month",
};
const renewalTermLabel = (v: string | null | undefined): string =>
  v ? (RENEWAL_TERM_LABEL[v] ?? v) : "—";

function Loading() {
  return (
    <div className="space-y-3 p-4">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-24 w-full" />
      ))}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return <div className="p-6 text-sm text-destructive">{message}</div>;
}

// ── Contract detail (rich) ───────────────────────────────────────────────────
function RelatedList({
  label,
  refs,
  onOpen,
}: {
  label: string;
  refs: RelatedContractRef[];
  onOpen: (id: string) => void;
}) {
  if (refs.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="flex flex-col gap-0.5">
        {refs.map((r) => (
          <NavLink key={r.id} onClick={() => onOpen(r.id)}>
            {str(r.company_name)} — Signed {fmtDate(r.sign_date)}
          </NavLink>
        ))}
      </dd>
    </div>
  );
}

/** Replacement contracts create (or contract) value; commission is paid on that increase
 *  over the greater of what the account was billed last month or the replaced contract's
 *  ARR — not the replacement's face value. Shown in both quick + expanded drawer states. */
function ReplacementValueSection({ id }: { id: string }) {
  const q = useReplacementValue(id, true);
  const v = q.data;
  // fmtCur renders 0/null as "—"; only prefix "+"/"−" sign onto a real non-zero value.
  const signed = (n: number | null | undefined) =>
    n == null || n === 0 ? fmtCur(n) : (n > 0 ? "+" : "") + fmtCur(n);
  // A monthly figure shown with its annualized (×12) equivalent.
  const mrrArr = (n: number | null | undefined) =>
    n == null ? "—" : `${fmtCur(n)}/mo · ${fmtCur(n * 12)} ARR`;
  const tone = (n: number | null | undefined) =>
    n == null ? "" : n < 0 ? "text-danger" : "text-success";

  return (
    <Section title="Replacement Value">
      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading billing actuals…</p>
      ) : q.error || !v ? (
        <p className="text-sm text-muted-foreground">Couldn't load replacement value.</p>
      ) : (
        <>
          {/* Headline: the value the replacement created = increase over the greater-of
              baseline. This IS the commission basis. Negative = contraction. */}
          <Grid>
            <Field label={(v.new_value_mrr ?? 0) < 0 ? "Contraction (MRR)" : "New Value (MRR)"}>
              <span className={`text-base font-semibold ${tone(v.new_value_mrr)}`}>
                {signed(v.new_value_mrr)}
              </span>
            </Field>
            <Field label={(v.new_value_arr ?? 0) < 0 ? "Contraction (ARR)" : "New Value (ARR)"}>
              <span className={`text-base font-semibold ${tone(v.new_value_arr)}`}>
                {signed(v.new_value_arr)}
              </span>
            </Field>
          </Grid>
          <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-border pt-3 text-xs">
            <dt className="text-muted-foreground">Replacement MRR (face)</dt>
            <dd className="text-right tabular-nums">{mrrArr(v.replacement_mrr)}</dd>
            <dt className="text-muted-foreground">
              Prior month billed{v.prior_month_period ? ` (${v.prior_month_period})` : ""}
            </dt>
            <dd className="text-right tabular-nums">{mrrArr(v.prior_month_billed_mrr)}</dd>
            <dt className="text-muted-foreground">Replaced contract MRR</dt>
            <dd className="text-right tabular-nums">{mrrArr(v.replaced_mrr)}</dd>
            <dt className="text-muted-foreground">Baseline (greater-of)</dt>
            <dd className="text-right tabular-nums">{mrrArr(v.baseline_monthly)}</dd>
          </dl>
          <p className="mt-3 text-xs text-muted-foreground">
            New value = replacement ARR − the greater of (prior month billed × 12) or (replaced
            contract ARR). Commission is paid on this increase in value, not the face value of the
            new contract.
            {v.data_source === "stale-cache" ? " (cached actuals)" : ""}
            {v.data_source === "unavailable"
              ? " Billing actuals unavailable; baseline uses the replaced contract MRR only."
              : ""}
          </p>
        </>
      )}
    </Section>
  );
}

function ContractDetailView({
  id,
  expanded,
  onOpenDetail,
}: {
  id: string;
  expanded: boolean;
  onOpenDetail: OpenDetail;
}) {
  const q = useContractDetail(id);
  const c: ContractDetail | undefined = q.data;
  const previousId = c?.replacement_contract_details?.id ?? null;
  // The replaced contract's full detail drives the PC (previous-contract) timeline lead-in.
  const prevQ = useContractDetail(previousId);
  if (q.isLoading) return <Loading />;
  if (q.error || !c) return <ErrorState message="Couldn't load this contract." />;
  const isReplacement = Boolean(c.replacement_contract_details);
  const lineItems = c.contract_line_items ?? [];
  const subtotal = lineItems.reduce((s, li) => s + Number(li.item_total ?? 0), 0);
  const total = subtotal + Number(c.base_fee ?? 0);

  const single = (r: RelatedContractRef | null | undefined): RelatedContractRef[] => (r ? [r] : []);
  const hasRelated =
    (c.replaced_by_contracts?.length ||
      c.replacement_contract_details ||
      c.amended_by_contracts?.length ||
      c.amendment_contract_details ||
      c.reseller_by_contracts?.length ||
      c.reseller_contract_details ||
      c.assigned_company_id) != null &&
    Boolean(
      c.replaced_by_contracts?.length ||
        c.replacement_contract_details ||
        c.amended_by_contracts?.length ||
        c.amendment_contract_details ||
        c.reseller_by_contracts?.length ||
        c.reseller_contract_details ||
        c.assigned_company_id,
    );

  return (
    <div className="space-y-4 p-4">
      <div className="flex justify-end">
        <CreateProposalFromContractButton contract={c} />
      </div>

      <Section title="Overview">
        <Grid>
          <Field label="MRR">{fmtCur2(c.mrr_total)}</Field>
          <Field label="ARR">{fmtCur2((c.mrr_total ?? 0) * 12)}</Field>
          <Field label="Term">{c.term_months ? `${c.term_months} months` : "—"}</Field>
          <Field label="Payment">{str(c.payment_type)}</Field>
          <Field label="Total Contract Value">{fmtCur2(total)}</Field>
          {c.contract_status && (
            <Field label="Status">
              <StatusBadge value={c.contract_status.toLowerCase()} />
            </Field>
          )}
        </Grid>
      </Section>

      {isReplacement && <ReplacementValueSection id={id} />}

      {expanded && (
        <ContractTimelineChart contract={c} previous={isReplacement ? prevQ.data : null} />
      )}

      <Section title="Company Information">
        <Grid>
          <Field label="Company">{str(c.company_name)}</Field>
          <Field label="DBA">{str(c.company_dba)}</Field>
          <Field label="Account">
            {c.account ? (
              <NavLink onClick={() => onOpenDetail("accounts", c.account!)}>
                {str(c.account_name)}
              </NavLink>
            ) : (
              str(c.account_name)
            )}
          </Field>
          <Field label="Contract Type">{str(c.contract_type_name)}</Field>
          <Field label="Billing From">{fmtDate(c.billing_effective_from)}</Field>
          <Field label="Billing Through">{fmtDate(c.billing_effective_through)}</Field>
          <Field label="Sign Date">{fmtDate(c.sign_date)}</Field>
          <Field label="Renewal Date">{fmtDate(c.renewal_date)}</Field>
        </Grid>
      </Section>

      {hasRelated && (
        <Section title="Related Contracts">
          <div className="flex flex-col gap-3">
            <RelatedList
              label="Replaced By"
              refs={c.replaced_by_contracts ?? []}
              onOpen={(rid) => onOpenDetail("contracts", rid)}
            />
            <RelatedList
              label="Replaces"
              refs={single(c.replacement_contract_details)}
              onOpen={(rid) => onOpenDetail("contracts", rid)}
            />
            <RelatedList
              label="Amended By"
              refs={c.amended_by_contracts ?? []}
              onOpen={(rid) => onOpenDetail("contracts", rid)}
            />
            <RelatedList
              label="Amends"
              refs={single(c.amendment_contract_details)}
              onOpen={(rid) => onOpenDetail("contracts", rid)}
            />
            <RelatedList
              label="Reseller For"
              refs={c.reseller_by_contracts ?? []}
              onOpen={(rid) => onOpenDetail("contracts", rid)}
            />
            <RelatedList
              label="Reseller Contract"
              refs={single(c.reseller_contract_details)}
              onOpen={(rid) => onOpenDetail("contracts", rid)}
            />
            {c.assigned_company_id && (
              <div className="flex flex-col gap-1">
                <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Assigned Company
                </dt>
                <dd>
                  <NavLink onClick={() => onOpenDetail("companies", c.assigned_company_id!)}>
                    {str(c.assigned_company_name)}
                  </NavLink>
                </dd>
              </div>
            )}
          </div>
        </Section>
      )}

      <Section title={`Line Items (${lineItems.length})`}>
        <div className="overflow-hidden rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead align="right">Qty</TableHead>
                <TableHead align="right">Unit Price</TableHead>
                <TableHead align="right">Overage</TableHead>
                <TableHead align="right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineItems.length === 0 ? (
                <TableEmpty colSpan={5}>No line items.</TableEmpty>
              ) : (
                lineItems.map((li, i) => (
                  <TableRow key={li.uuid ?? i}>
                    <TableCell>{str(li.item_detail?.name ?? li.package_detail?.name)}</TableCell>
                    <TableCell numeric>{li.qty != null ? Number(li.qty).toLocaleString() : "—"}</TableCell>
                    <TableCell numeric>{fmtCurExact(li.price_per_unit)}</TableCell>
                    <TableCell numeric>{fmtCurExact(li.overage_price_per_unit)}</TableCell>
                    <TableCell numeric>{fmtCur2(li.item_total)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Line items subtotal</dt>
          <dd className="text-right tabular-nums">{fmtCur2(subtotal)}</dd>
          <dt className="text-muted-foreground">Base fee</dt>
          <dd className="text-right tabular-nums">{fmtCur2(c.base_fee)}</dd>
          <dt className="font-medium">Total contract value</dt>
          <dd className="text-right font-medium tabular-nums">{fmtCur2(total)}</dd>
        </dl>
      </Section>

      {(c.contract_discounts?.length ?? 0) > 0 && (
        <ContractDiscountsSection discounts={c.contract_discounts ?? []} lineItems={lineItems} />
      )}

      <Section title="Pricing Structure">
        <Grid>
          <Field label="Base Fee">{fmtCur(c.base_fee)}</Field>
          <Field label="Initial Contacts">{str(c.initial_contacts)}</Field>
          <Field label="Fee per 10K More">{fmtCur(c.ten_k_more)}</Field>
          <Field label="Use Email Count">{yesNo(c.use_email_count)}</Field>
          <Field label="Custom Terms">{yesNo(c.custom_terms)}</Field>
        </Grid>
      </Section>

      <Section title="Terms & Renewal">
        <Grid>
          <Field label="Auto-renews">{yesNo(c.is_auto_renew)}</Field>
          <Field label="Renews Into">{renewalTermLabel(c.renewal_term)}</Field>
          {c.renewal_term === "DIFFERENT_TERM" && (
            <Field label="Renewal Term">
              {c.renewal_term_months ? `${c.renewal_term_months} months` : "—"}
            </Field>
          )}
          {c.renewal_term === "MONTH_TO_MONTH" && (
            <Field label="MTM Uplift">{c.mtm_uplift_pct != null ? `${c.mtm_uplift_pct}%` : "—"}</Field>
          )}
          <Field label="Notice Period">
            {c.notice_period_days != null ? `${c.notice_period_days} days` : "—"}
          </Field>
          <Field label="Next Renewal">{fmtDate(c.next_renewal_when)}</Field>
          <Field label="Debooked">{yesNo(c.debooked)}</Field>
          {c.debooked && <Field label="Debooked Reason">{str(c.debooked_reason)}</Field>}
          {c.additional_terms && <Field label="Additional Terms">{str(c.additional_terms)}</Field>}
        </Grid>
      </Section>

      <Section title="Primary Contact">
        <Grid>
          <Field label="Name">{str([c.first_name, c.last_name].filter(Boolean).join(" ") || null)}</Field>
          <Field label="Title">{str(c.title)}</Field>
          <Field label="Email">{str(c.email)}</Field>
          <Field label="Phone">{str(c.phone)}</Field>
          <Field label="CC Email">{str(c.cc_email)}</Field>
          <Field label="BCC Email">{str(c.bcc_email)}</Field>
        </Grid>
      </Section>

      {(c.street || c.city || c.state || c.zip) && (
        <Section title="Address">
          <Grid>
            <Field label="Street">{str(c.street)}</Field>
            <Field label="City">{str(c.city)}</Field>
            <Field label="State">{str(c.state)}</Field>
            <Field label="Zip">{str(c.zip)}</Field>
          </Grid>
        </Section>
      )}

      <Section title="Sales">
        <Grid>
          <Field label="Closer">{str(c.closer_name)}</Field>
          <Field label="Closer HubSpot Owner">{str(c.closer_hubspot_owner_id)}</Field>
          <Field label="PandaDoc">
            {c.pandadoc_id ? <ExtLink href={pandadocUrl(c.pandadoc_id)}>{c.pandadoc_id}</ExtLink> : "—"}
          </Field>
          <Field label="HubSpot Deal">
            {c.hubspot_deal_id ? (
              <ExtLink href={hubspotDealUrl(c.hubspot_deal_id)}>{c.hubspot_deal_id}</ExtLink>
            ) : (
              "—"
            )}
          </Field>
          <Field label="Stripe Customer">
            {c.stripe_customer_id ? (
              <ExtLink href={stripeCustomerUrl(c.stripe_customer_id)}>{c.stripe_customer_id}</ExtLink>
            ) : (
              "—"
            )}
          </Field>
        </Grid>
      </Section>
    </div>
  );
}

// ── Account detail (rich) ────────────────────────────────────────────────────
function AccountDetailView({ id, onOpenDetail }: { id: string; onOpenDetail: OpenDetail }) {
  const q = useAccountDetail(id);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorState message="Couldn't load this account." />;
  const a = q.data;
  const companies = a.companies ?? [];
  const contracts = a.contracts ?? [];
  const activeContracts = contracts.filter((c) => isActiveThrough(c.billing_effective_through));
  const totalMrr = activeContracts.reduce((s, c) => s + (c.mrr_total ?? 0), 0);

  return (
    <div className="space-y-4 p-4">
      <Section title="Overview">
        <Grid>
          <Field label="Status">
            <StatusBadge value={activeContracts.length > 0 ? "active" : "inactive"} />
          </Field>
          <Field label="Total MRR (active)">{fmtCur(totalMrr)}</Field>
          <Field label="Companies">{companies.length}</Field>
          <Field label="QuickBooks ID">
            {a.quickbooks_id != null ? (
              <ExtLink href={qboCustomerUrl(a.quickbooks_id)}>{a.quickbooks_id}</ExtLink>
            ) : (
              "—"
            )}
          </Field>
          <Field label="QuickBooks Customer">{str(a.quickbooks_customer_name)}</Field>
        </Grid>
      </Section>

      <Section title="Account Info">
        <Grid>
          <Field label="Name">{str(a.name)}</Field>
          <Field label="ID">{str(a.id)}</Field>
          <Field label="Created">{fmtDate(a.created_when)}</Field>
          <Field label="Updated">{fmtDate(a.updated_when)}</Field>
        </Grid>
      </Section>

      <Section title={`Companies (${companies.length})`}>
        {companies.length === 0 ? (
          <p className="text-sm text-muted-foreground">No companies.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {companies.map((co) => (
              <button
                key={co.id}
                type="button"
                onClick={() => onOpenDetail("companies", co.id)}
                className="inline-flex items-center rounded-full border border-border bg-muted px-2.5 py-1 text-xs hover:border-brand hover:text-brand"
              >
                {str(co.name)}
              </button>
            ))}
          </div>
        )}
      </Section>

      <Section title={`Contracts (${contracts.length})`}>
        <div className="overflow-hidden rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Company</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Sign Date</TableHead>
                <TableHead align="right">MRR</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody zebra>
              {contracts.length === 0 ? (
                <TableEmpty colSpan={4}>No contracts.</TableEmpty>
              ) : (
                contracts.map((c) => (
                  <TableRow key={c.id} clickable onClick={() => onOpenDetail("contracts", c.id)}>
                    <TableCell>{str(c.company_name)}</TableCell>
                    <TableCell>
                      <StatusBadge value={isActiveThrough(c.billing_effective_through) ? "active" : "inactive"} />
                    </TableCell>
                    <TableCell>{fmtDate(c.sign_date)}</TableCell>
                    <TableCell numeric>{fmtCur(c.mrr_total)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Section>
    </div>
  );
}

// ── Company detail ───────────────────────────────────────────────────────────
function CompanyDetailView({ id, onOpenDetail }: { id: string; onOpenDetail: OpenDetail }) {
  const q = useCompanyDetail(id);
  if (q.isLoading) return <Loading />;
  if (q.error || !q.data) return <ErrorState message="Couldn't load this company." />;
  const co = q.data;
  return (
    <div className="space-y-4 p-4">
      <Section title="Company">
        <Grid>
          <Field label="Name">{str(co.name)}</Field>
          <Field label="Account">
            {co.account ? (
              <NavLink onClick={() => onOpenDetail("accounts", co.account!)}>
                {str(co.account_name)}
              </NavLink>
            ) : (
              str(co.account_name)
            )}
          </Field>
          <Field label="Status">
            <StatusBadge value={co.is_active ? "active" : "inactive"} />
          </Field>
          <Field label="Phone">{str(co.phone)}</Field>
          <Field label="Created">{fmtDate(co.created_when)}</Field>
          <Field label="Updated">{fmtDate(co.updated_when)}</Field>
        </Grid>
      </Section>

      {(co.address1 || co.city || co.state || co.zip_code) && (
        <Section title="Address">
          <Grid>
            <Field label="Street">{str(co.address1)}</Field>
            <Field label="City">{str(co.city)}</Field>
            <Field label="State">{str(co.state)}</Field>
            <Field label="Zip">{str(co.zip_code)}</Field>
          </Grid>
        </Section>
      )}
    </div>
  );
}

// ── Contract type (quick = record; expanded = lifecycle playbook) ────────────
function ContractTypeDetail({ id, expanded }: { id: string; expanded: boolean }) {
  // The contract-types list is small and already cached (the Contract Type
  // filter options use the same query) — find the row by id; no detail endpoint.
  const q = useContractTypes({ limit: 500, offset: 0 });
  // Admin-edited overrides (Ops-persisted); absent → emulated archetype default.
  const playbooks = useContractTypePlaybooks();
  if (q.isLoading) return <Loading />;
  const row = (q.data?.results ?? []).find((t) => t.id === id);
  if (!row) return <ErrorState message="Couldn't load this contract type." />;
  const stored = playbooks.data?.[id];
  return expanded ? (
    <ContractTypePlaybookView row={row} stored={stored} />
  ) : (
    <ContractTypeQuickView row={row} />
  );
}

// ── Contract discounts (nested under a contract) ─────────────────────────────
function ContractDiscountsSection({
  discounts,
  lineItems,
}: {
  discounts: ContractDiscount[];
  lineItems: { uuid?: string; item_detail?: { name?: string | null } | null; package_detail?: { name?: string | null } | null }[];
}) {
  const lineName = (uuid?: string | null): string => {
    if (!uuid) return "Whole contract";
    const li = lineItems.find((l) => l.uuid === uuid);
    return li?.item_detail?.name ?? li?.package_detail?.name ?? "Line item";
  };
  return (
    <Section title={`Discounts (${discounts.length})`}>
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Applies To</TableHead>
              <TableHead align="right">Duration</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {discounts.map((d, i) => {
              const det = d.discount_detail;
              return (
                <TableRow key={i}>
                  <TableCell>{discountTypeLabel(det?.type)}</TableCell>
                  <TableCell>
                    {discountValueLabel({
                      type: det?.type ?? null,
                      value: det?.value ?? null,
                      duration_periods: det?.duration_periods ?? null,
                      period_unit: null,
                    })}
                  </TableCell>
                  <TableCell>{lineName(d.target_line_item)}</TableCell>
                  <TableCell numeric>
                    {d.duration_override ?? det?.duration_periods ?? "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Section>
  );
}

// ── Package detail (row-based; nested package_items) ─────────────────────────
function PackageDetailView({ row }: { row: Record<string, unknown> }) {
  const p = row as unknown as PackageRow;
  const items = p.package_items ?? [];
  return (
    <div className="space-y-4 p-4">
      <Section title="Package">
        <Grid>
          <Field label="Name">{str(p.name)}</Field>
          <Field label="SKU">{str(p.sku)}</Field>
          <Field label="Pricing Type">{pricingTypeLabel(p.pricing_type)}</Field>
          <Field label="Price">{fmtCur(p.price)}</Field>
          <Field label="Est. Gross Margin">{marginPct(p.estimated_gross_margin)}</Field>
          <Field label="Price Metric">{str(p.price_metric_name)}</Field>
          <Field label="Proposal Eligible">{yesNo(p.proposal_eligible)}</Field>
          <Field label="Archived">{yesNo(p.is_archived)}</Field>
        </Grid>
        {(p.descr || p.customer_facing_descr) && (
          <Grid>
            <Field label="Internal Description">{str(p.descr)}</Field>
            <Field label="Customer-facing Description">{str(p.customer_facing_descr)}</Field>
          </Grid>
        )}
      </Section>

      <Section title={`Included Items (${items.length})`}>
        <div className="overflow-hidden rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead align="right">Qty Limit</TableHead>
                <TableHead align="right">Included Price</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableEmpty colSpan={3}>No included items.</TableEmpty>
              ) : (
                items.map((pi, i) => (
                  <TableRow key={pi.id ?? i}>
                    <TableCell>{str(pi.item_detail?.name)}</TableCell>
                    <TableCell numeric>
                      {pi.display_as_unlimited
                        ? "Unlimited"
                        : pi.quantity_limit != null
                          ? Number(pi.quantity_limit).toLocaleString()
                          : "—"}
                    </TableCell>
                    <TableCell numeric>{fmtCur(pi.included_price)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Section>
    </div>
  );
}

// ── Discount detail (row-based; nested unit_grants) ──────────────────────────
function DiscountDetailView({ row }: { row: Record<string, unknown> }) {
  const d = row as unknown as DiscountRow;
  const grants = d.unit_grants ?? [];
  return (
    <div className="space-y-4 p-4">
      <Section title="Discount">
        <Grid>
          <Field label="Name">{str(d.name)}</Field>
          <Field label="Type">{discountTypeLabel(d.type)}</Field>
          <Field label="Value">{discountValueLabel(d)}</Field>
          <Field label="Code">{str(d.code)}</Field>
          <Field label="Stackable">{yesNo(d.stackable)}</Field>
          <Field label="Archived">{yesNo(d.is_archived)}</Field>
          {d.type === "time_based" && (
            <>
              <Field label="Duration">
                {d.duration_periods != null ? `${d.duration_periods} ${d.period_unit || "month"}(s)` : "—"}
              </Field>
              <Field label="Window">
                {d.start_date || d.end_date
                  ? `${fmtDate(d.start_date)} → ${fmtDate(d.end_date)}`
                  : "—"}
              </Field>
            </>
          )}
          {d.type === "quantity_based" && (
            <>
              <Field label="Min Quantity">{str(d.min_quantity)}</Field>
              <Field label="Max Quantity">{str(d.max_quantity)}</Field>
            </>
          )}
        </Grid>
        {(d.description || d.customer_facing_descr) && (
          <Grid>
            <Field label="Internal Description">{str(d.description)}</Field>
            <Field label="Customer-facing Description">{str(d.customer_facing_descr)}</Field>
          </Grid>
        )}
      </Section>

      {grants.length > 0 && (
        <Section title={`Unit Grants (${grants.length})`}>
          <div className="overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Price Metric</TableHead>
                  <TableHead align="right">Quantity</TableHead>
                  <TableHead>Recurrence</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grants.map((g, i) => (
                  <TableRow key={g.id ?? i}>
                    <TableCell>{str(g.price_metric)}</TableCell>
                    <TableCell numeric>{g.quantity != null ? Number(g.quantity).toLocaleString() : "—"}</TableCell>
                    <TableCell>{str(g.recurrence)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Section>
      )}
    </div>
  );
}

// ── Generic field view (tabs with no detail endpoint; row-based) ─────────────
type FieldDef = { label: string; render: (row: Record<string, unknown>) => ReactNode };

const FIELD_CONFIG: Partial<Record<ContractsTabKey, FieldDef[]>> = {
  quickbooks: [
    { label: "Company", render: (r) => str(r.CompanyName) },
    {
      label: "Contact",
      render: (r) => str([r.GivenName, r.FamilyName].filter(Boolean).join(" ") || null),
    },
    { label: "Email", render: (r) => str(r.PrimaryEmailAddr) },
    { label: "Cinch Account", render: (r) => str((r.account as { name?: string } | null)?.name) },
    {
      label: "QuickBooks ID",
      render: (r) =>
        r.Id ? <ExtLink href={qboCustomerUrl(String(r.Id))}>{String(r.Id)}</ExtLink> : "—",
    },
  ],
  items: [
    { label: "Item", render: (r) => str(r.name) },
    { label: "Category", render: (r) => categoryLabel(r.category as string) },
    { label: "Integrations", render: (r) => integrationLabels(r.integration_keys as string[]) },
    { label: "Price Metric", render: (r) => str(r.price_metric_name) },
    { label: "Unit Cost", render: (r) => fmtCur(r.unit_cost as number) },
    { label: "Floor Price", render: (r) => fmtCur(r.floor_price as number) },
    { label: "Starting Price", render: (r) => fmtCur(r.starting_price as number) },
    { label: "Recommended Price", render: (r) => fmtCur(r.recommended_price as number) },
    { label: "SKU (Cinch)", render: (r) => str(r.sku_cinch) },
    { label: "Internal Description", render: (r) => str(r.descr) },
    { label: "Customer-facing Description", render: (r) => str(r.customer_facing_descr) },
    { label: "Archived", render: (r) => yesNo(r.is_archived) },
  ],
  "contract-types": [
    { label: "Name", render: (r) => str(r.name) },
    { label: "Description", render: (r) => str(r.descr) },
    { label: "Contracts Using", render: (r) => str(r.contract_count) },
  ],
  "price-metrics": [
    { label: "Name", render: (r) => str(r.name) },
    { label: "Description", render: (r) => str(r.description) },
    { label: "DB Reference", render: (r) => str(r.id) },
    { label: "Support Tiers", render: (r) => yesNo(r.support_tiers) },
  ],
};

function FieldView({ kind, row }: { kind: ContractsTabKey; row: Record<string, unknown> }) {
  const fields = FIELD_CONFIG[kind] ?? [];
  return (
    <div className="p-4">
      <Section title="Details">
        <Grid>
          {fields.map((f) => (
            <Field key={f.label} label={f.label}>
              {f.render(row)}
            </Field>
          ))}
        </Grid>
      </Section>
    </div>
  );
}

// ── Drawer host ──────────────────────────────────────────────────────────────
const TITLES: Record<ContractsTabKey, string> = {
  contracts: "Contract",
  accounts: "Account",
  companies: "Company",
  quickbooks: "QuickBooks Customer",
  items: "Item",
  packages: "Package",
  discounts: "Discount",
  "contract-types": "Contract Type",
  "price-metrics": "Price Metric",
};

function subtitleFor(sel: Selection): string {
  // URL-driven kinds carry no row; the detail content shows the name.
  const r = sel.row;
  if (sel.kind === "quickbooks") return r.CompanyName ? String(r.CompanyName) : "";
  return r.name ? String(r.name) : "";
}

export function RecordDrawer({
  selection,
  expanded,
  onExpandedChange,
  onClose,
  onOpenDetail,
}: {
  selection: Selection | null;
  expanded: boolean;
  onExpandedChange: (v: boolean) => void;
  onClose: () => void;
  onOpenDetail: OpenDetail;
}) {
  return (
    <EntityDrawer
      open={selection != null}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      onClose={onClose}
      title={selection ? TITLES[selection.kind] : ""}
      subtitle={selection ? subtitleFor(selection) : ""}
    >
      {selection?.kind === "contracts" && (
        <ContractDetailView id={selection.id} expanded={expanded} onOpenDetail={onOpenDetail} />
      )}
      {selection?.kind === "accounts" && (
        <AccountDetailView id={selection.id} onOpenDetail={onOpenDetail} />
      )}
      {selection?.kind === "companies" && (
        <CompanyDetailView id={selection.id} onOpenDetail={onOpenDetail} />
      )}
      {selection?.kind === "contract-types" && (
        <ContractTypeDetail id={selection.id} expanded={expanded} />
      )}
      {selection?.kind === "packages" && <PackageDetailView row={selection.row} />}
      {selection?.kind === "discounts" && <DiscountDetailView row={selection.row} />}
      {selection != null &&
        selection.kind !== "contracts" &&
        selection.kind !== "accounts" &&
        selection.kind !== "companies" &&
        selection.kind !== "contract-types" &&
        selection.kind !== "packages" &&
        selection.kind !== "discounts" && (
          <FieldView kind={selection.kind} row={selection.row} />
        )}
    </EntityDrawer>
  );
}
